/**
 * Computed Nodes - Derived Values
 *
 * These are calculated from sources and assumptions.
 * Implements the full 10-year financial model.
 */

import { computed, ComputedNode } from '../../lib/computation-graph';
import { temporalId, compoundGrowth } from '../../lib/computation-graph/temporal';

const YEARS = 11; // 0-10

/**
 * Helper to create year-indexed computed nodes
 */
function forEachYear(
  fn: (year: number) => Record<string, ComputedNode>
): Record<string, ComputedNode> {
  const result: Record<string, ComputedNode> = {};
  for (let year = 0; year < YEARS; year++) {
    Object.assign(result, fn(year));
  }
  return result;
}

// ============================================================================
// BUILDING VALUE & DEPRECIATION
// ============================================================================

export const buildingNodes: Record<string, ComputedNode> = {
  // Building value (25% of property value for coastal CA)
  buildingValue: computed('buildingValue', ['propertyValue', 'coastalBuildingRatio'], (pv, ratio) => pv * ratio, {
    label: 'Building Value',
    description: 'Depreciable portion of property (building only, not land)',
    unit: 'currency',
    formula: '{propertyValue} × {coastalBuildingRatio}',
  }),

  // Annual depreciation deduction
  annualDepreciation: computed(
    'annualDepreciation',
    ['buildingValue', 'residentialDepreciationYears'],
    (building, years) => building / years,
    {
      label: 'Annual Depreciation',
      description: 'Yearly depreciation deduction for rental property',
      unit: 'currency',
      formula: '{buildingValue} / {residentialDepreciationYears}',
    }
  ),

  // Step-up basis (equals property value at inheritance)
  stepUpBasis: computed('stepUpBasis', ['propertyValue'], (pv) => pv, {
    label: 'Step-Up Basis',
    description: 'Cost basis after inheritance (fair market value at death)',
    unit: 'currency',
    formula: '{propertyValue}',
  }),

  // Initial sale proceeds (if selling immediately)
  initialSaleProceeds: computed(
    'initialSaleProceeds',
    ['propertyValue', 'sellingCosts'],
    (pv, sc) => pv * (1 - sc / 100),
    {
      label: 'Net Sale Proceeds',
      description: 'Amount received after selling costs',
      unit: 'currency',
      formula: '{propertyValue} × (1 - {sellingCosts}/100)',
    }
  ),
};

// ============================================================================
// TAX DRAG CALCULATIONS
// ============================================================================

export const taxDragNodes: Record<string, ComputedNode> = {
  // Dividend tax drag on stocks
  equityDividendDrag: computed(
    'equityDividendDrag',
    ['sp500DividendYield', 'capitalGainsRate'],
    (yield_, rate) => (yield_ / 100) * (rate / 100),
    {
      label: 'Equity Dividend Drag',
      description: 'Annual tax drag from dividend taxation',
      unit: 'ratio',
      formula: '{sp500DividendYield}/100 × {capitalGainsRate}/100',
    }
  ),

  // Interest tax drag on bonds
  bondInterestDrag: computed(
    'bondInterestDrag',
    ['bondYieldAssumption', 'rentalIncomeTaxRate'],
    (yield_, rate) => (yield_ / 100) * (rate / 100),
    {
      label: 'Bond Interest Drag',
      description: 'Annual tax drag from bond interest taxation',
      unit: 'ratio',
      formula: '{bondYieldAssumption}/100 × {rentalIncomeTaxRate}/100',
    }
  ),

  // Effective stock return after tax drag
  effectiveStockReturn: computed(
    'effectiveStockReturn',
    ['stockReturns', 'equityDividendDrag'],
    (returns, drag) => returns - drag * 100,
    {
      label: 'Effective Stock Return',
      description: 'Stock returns after dividend tax drag',
      unit: 'percent',
      formula: '{stockReturns} - {equityDividendDrag}×100',
    }
  ),

  // Effective bond return after tax drag
  effectiveBondReturn: computed(
    'effectiveBondReturn',
    ['bondReturns', 'bondInterestDrag'],
    (returns, drag) => returns - drag * 100,
    {
      label: 'Effective Bond Return',
      description: 'Bond returns after interest tax drag',
      unit: 'percent',
      formula: '{bondReturns} - {bondInterestDrag}×100',
    }
  ),

  // Blended return for 80/20 portfolio
  blendedReturn: computed(
    'blendedReturn',
    ['effectiveStockReturn', 'effectiveBondReturn'],
    (stock, bond) => 0.8 * stock + 0.2 * bond,
    {
      label: 'Blended Return (80/20)',
      description: 'Weighted return for 80% stocks / 20% bonds',
      unit: 'percent',
      formula: '0.8 × {effectiveStockReturn} + 0.2 × {effectiveBondReturn}',
    }
  ),

  // Portfolio return rates for different allocations
  portfolio100Return: computed(
    'portfolio100Return',
    ['effectiveStockReturn'],
    (stock) => stock,
    {
      label: '100% Stocks Return',
      unit: 'percent',
      formula: '{effectiveStockReturn}',
    }
  ),

  portfolio80_20Return: computed(
    'portfolio80_20Return',
    ['effectiveStockReturn', 'effectiveBondReturn'],
    (stock, bond) => 0.8 * stock + 0.2 * bond,
    {
      label: '80/20 Portfolio Return',
      unit: 'percent',
      formula: '0.8 × {effectiveStockReturn} + 0.2 × {effectiveBondReturn}',
    }
  ),

  portfolio60_40Return: computed(
    'portfolio60_40Return',
    ['effectiveStockReturn', 'effectiveBondReturn'],
    (stock, bond) => 0.6 * stock + 0.4 * bond,
    {
      label: '60/40 Portfolio Return',
      unit: 'percent',
      formula: '0.6 × {effectiveStockReturn} + 0.4 × {effectiveBondReturn}',
    }
  ),
};

// ============================================================================
// PROPERTY VALUE BY YEAR
// Using compoundGrowth for series metadata and simpler derivation trees
// ============================================================================

export const propertyValueNodes: Record<string, ComputedNode> = compoundGrowth('propertyValue', {
  initialValueId: 'propertyValue',
  growthRateId: 'propertyAppreciation',
  label: 'Property Value',
  unit: 'currency',
  years: YEARS,
});

// ============================================================================
// ASSESSED VALUE BY YEAR (PROP 13)
// ============================================================================

export const assessedValueNodes: Record<string, ComputedNode> = {
  // Nominal appreciation for Prop 13 comparison
  nominalAppreciation: computed(
    'nominalAppreciation',
    ['propertyAppreciation', 'inflationRate'],
    (real, inflation) => real + inflation,
    {
      label: 'Nominal Appreciation',
      description: 'Real appreciation plus inflation',
      unit: 'percent',
      formula: '{propertyAppreciation} + {inflationRate}',
    }
  ),

  // Prop 13 growth rate (capped at 2%)
  assessedGrowthRate: computed(
    'assessedGrowthRate',
    ['nominalAppreciation', 'prop13MaxGrowth'],
    (nominal, cap) => Math.min(nominal, cap),
    {
      label: 'Assessed Value Growth Rate',
      description: 'Prop 13 caps growth at 2% nominal',
      unit: 'percent',
      formula: 'min({nominalAppreciation}, {prop13MaxGrowth})',
    }
  ),

  // Year-by-year assessed values using compoundGrowth for series metadata
  ...compoundGrowth('assessedValue', {
    initialValueId: 'propertyValue',
    growthRateId: 'assessedGrowthRate',
    label: 'Assessed Value',
    unit: 'currency',
    years: YEARS,
  }),
};

// ============================================================================
// RENTAL INCOME BY YEAR
// ============================================================================

export const rentalNodes: Record<string, ComputedNode> = forEachYear((year) => {
  const pvId = temporalId('propertyValue', year);
  const avId = temporalId('assessedValue', year);

  const nodes: Record<string, ComputedNode> = {};

  // Annual rent
  const annualRentId = temporalId('annualRent', year);
  if (year === 0) {
    nodes[annualRentId] = computed(annualRentId, ['monthlyRent'], (mr) => mr * 12, {
      label: `Annual Rent (Year ${year})`,
      unit: 'currency',
      formula: '{monthlyRent} × 12',
    });
  } else {
    const prevRentId = temporalId('annualRent', year - 1);
    nodes[annualRentId] = computed(
      annualRentId,
      [prevRentId, 'rentGrowth'],
      (prev, rate) => prev * (1 + rate / 100),
      {
        label: `Annual Rent (Year ${year})`,
        unit: 'currency',
        formula: `{${prevRentId}} × (1 + {rentGrowth}/100)`,
      }
    );
  }

  // Vacancy loss
  const vacancyLossId = temporalId('vacancyLoss', year);
  nodes[vacancyLossId] = computed(vacancyLossId, [annualRentId, 'vacancyRate'], (rent, rate) => rent * rate / 100, {
    label: `Vacancy Loss (Year ${year})`,
    unit: 'currency',
    formula: `{${annualRentId}} × {vacancyRate}/100`,
  });

  // Gross rental income (after vacancy)
  const grossRentId = temporalId('grossRentalIncome', year);
  nodes[grossRentId] = computed(grossRentId, [annualRentId, vacancyLossId], (rent, vacancy) => rent - vacancy, {
    label: `Gross Rental Income (Year ${year})`,
    unit: 'currency',
    formula: `{${annualRentId}} - {${vacancyLossId}}`,
  });

  // Property tax (based on ASSESSED value, not market value - Prop 13)
  const propertyTaxId = temporalId('propertyTax', year);
  nodes[propertyTaxId] = computed(propertyTaxId, [avId, 'propertyTaxRate'], (av, rate) => av * rate / 100, {
    label: `Property Tax (Year ${year})`,
    unit: 'currency',
    formula: `{${avId}} × {propertyTaxRate}/100`,
  });

  // Maintenance (based on market value)
  const maintenanceId = temporalId('maintenance', year);
  nodes[maintenanceId] = computed(maintenanceId, [pvId, 'maintenanceRate'], (pv, rate) => pv * rate / 100, {
    label: `Maintenance (Year ${year})`,
    unit: 'currency',
    formula: `{${pvId}} × {maintenanceRate}/100`,
  });

  // Management fee (based on effective rent after vacancy)
  const managementId = temporalId('managementCost', year);
  nodes[managementId] = computed(managementId, [grossRentId, 'managementFee'], (gross, rate) => gross * rate / 100, {
    label: `Management Cost (Year ${year})`,
    unit: 'currency',
    formula: `{${grossRentId}} × {managementFee}/100`,
  });

  // Total expenses
  const totalExpensesId = temporalId('totalExpenses', year);
  nodes[totalExpensesId] = computed(
    totalExpensesId,
    [propertyTaxId, 'insuranceCost', maintenanceId, vacancyLossId, managementId],
    (tax, insurance, maint, vacancy, mgmt) => tax + insurance + maint + vacancy + mgmt,
    {
      label: `Total Expenses (Year ${year})`,
      unit: 'currency',
      formula: `{${propertyTaxId}} + {insuranceCost} + {${maintenanceId}} + {${vacancyLossId}} + {${managementId}}`,
    }
  );

  // Net rental income (before tax)
  const netRentalId = temporalId('netRentalIncome', year);
  nodes[netRentalId] = computed(
    netRentalId,
    [grossRentId, propertyTaxId, 'insuranceCost', maintenanceId, managementId],
    (gross, tax, insurance, maint, mgmt) => gross - (tax + insurance + maint + mgmt),
    {
      label: `Net Rental Income (Year ${year})`,
      unit: 'currency',
      formula: `{${grossRentId}} - ({${propertyTaxId}} + {insuranceCost} + {${maintenanceId}} + {${managementId}})`,
    }
  );

  // Depreciation deduction (if enabled)
  const depreciationId = temporalId('depreciationDeduction', year);
  nodes[depreciationId] = computed(
    depreciationId,
    ['annualDepreciation', 'depreciation'],
    (annual, enabled) => enabled ? annual : 0,
    {
      label: `Depreciation Deduction (Year ${year})`,
      unit: 'currency',
      formula: '{depreciation} ? {annualDepreciation} : 0',
    }
  );

  // Taxable rental income
  const taxableId = temporalId('taxableRentalIncome', year);
  nodes[taxableId] = computed(
    taxableId,
    [netRentalId, depreciationId],
    (net, dep) => Math.max(0, net - dep),
    {
      label: `Taxable Rental Income (Year ${year})`,
      unit: 'currency',
      formula: `max(0, {${netRentalId}} - {${depreciationId}})`,
    }
  );

  // Rental income tax
  const rentalTaxId = temporalId('rentalIncomeTax', year);
  nodes[rentalTaxId] = computed(
    rentalTaxId,
    [taxableId, 'rentalIncomeTaxRate'],
    (taxable, rate) => taxable * rate / 100,
    {
      label: `Rental Income Tax (Year ${year})`,
      unit: 'currency',
      formula: `{${taxableId}} × {rentalIncomeTaxRate}/100`,
    }
  );

  // Net rental income after tax
  const netAfterTaxId = temporalId('netRentalIncomeAfterTax', year);
  nodes[netAfterTaxId] = computed(
    netAfterTaxId,
    [netRentalId, rentalTaxId],
    (net, tax) => net - tax,
    {
      label: `Net Rental Income After Tax (Year ${year})`,
      unit: 'currency',
      formula: `{${netRentalId}} - {${rentalTaxId}}`,
    }
  );

  return nodes;
});

// ============================================================================
// CUMULATIVE VALUES
// ============================================================================

export const cumulativeNodes: Record<string, ComputedNode> = {
  // Cumulative depreciation
  ...forEachYear((year) => {
    const id = temporalId('cumulativeDepreciation', year);
    const depId = temporalId('depreciationDeduction', year);

    if (year === 0) {
      return {
        [id]: computed(id, [depId], (dep) => dep, {
          label: `Cumulative Depreciation (Year ${year})`,
          unit: 'currency',
          formula: `{${depId}}`,
        }),
      };
    }

    const prevId = temporalId('cumulativeDepreciation', year - 1);
    return {
      [id]: computed(id, [prevId, depId], (prev, dep) => prev + dep, {
        label: `Cumulative Depreciation (Year ${year})`,
        unit: 'currency',
        formula: `{${prevId}} + {${depId}}`,
      }),
    };
  }),

  // Invested rental portfolio (grows at blended return + annual contributions)
  ...forEachYear((year) => {
    const id = temporalId('investedRentalPortfolio', year);

    if (year === 0) {
      return {
        [id]: computed(id, [], () => 0, {
          label: `Invested Rental Portfolio (Year ${year})`,
          unit: 'currency',
          formula: '0',
        }),
      };
    }

    if (year === 1) {
      const netId = temporalId('netRentalIncomeAfterTax', 1);
      return {
        [id]: computed(id, [netId], (net) => net, {
          label: `Invested Rental Portfolio (Year ${year})`,
          unit: 'currency',
          formula: `{${netId}}`,
        }),
      };
    }

    const prevId = temporalId('investedRentalPortfolio', year - 1);
    const netId = temporalId('netRentalIncomeAfterTax', year);
    return {
      [id]: computed(id, [prevId, 'blendedReturn', netId], (prev, rate, net) => prev * (1 + rate / 100) + net, {
        label: `Invested Rental Portfolio (Year ${year})`,
        unit: 'currency',
        formula: `{${prevId}} × (1 + {blendedReturn}/100) + {${netId}}`,
      }),
    };
  }),
};

// ============================================================================
// SELL SCENARIO - PORTFOLIO VALUES
// Using compoundGrowth for series metadata and simpler derivation trees
// ============================================================================

export const sellScenarioNodes: Record<string, ComputedNode> = {
  // 100% stocks portfolio
  ...compoundGrowth('portfolio100', {
    initialValueId: 'initialSaleProceeds',
    growthRateId: 'portfolio100Return',
    label: '100% Stocks Portfolio',
    unit: 'currency',
    years: YEARS,
  }),

  // 80/20 portfolio
  ...compoundGrowth('portfolio80_20', {
    initialValueId: 'initialSaleProceeds',
    growthRateId: 'portfolio80_20Return',
    label: '80/20 Portfolio',
    unit: 'currency',
    years: YEARS,
  }),

  // 60/40 portfolio
  ...compoundGrowth('portfolio60_40', {
    initialValueId: 'initialSaleProceeds',
    growthRateId: 'portfolio60_40Return',
    label: '60/40 Portfolio',
    unit: 'currency',
    years: YEARS,
  }),
};

// ============================================================================
// EXIT CALCULATIONS (YEAR 10)
// ============================================================================

export const exitNodes: Record<string, ComputedNode> = {
  // Depreciation recapture tax
  depreciationRecaptureTax: computed(
    'depreciationRecaptureTax',
    [temporalId('cumulativeDepreciation', 10), 'federalDepreciationRecaptureRate', 'caStateTaxRate'],
    (cumDep, fedRate, caRate) => cumDep * (fedRate / 100 + caRate / 100),
    {
      label: 'Depreciation Recapture Tax',
      description: 'Tax on recaptured depreciation at exit',
      unit: 'currency',
      formula: '{cumulativeDepreciation.year10} × ({federalDepreciationRecaptureRate}/100 + {caStateTaxRate}/100)',
    }
  ),

  // Capital gains on property appreciation
  rentCapitalGainsTax: computed(
    'rentCapitalGainsTax',
    [temporalId('propertyValue', 10), 'stepUpBasis', 'capitalGainsRate'],
    (pv, basis, rate) => {
      const gain = pv - basis;
      return gain > 0 ? gain * rate / 100 : 0;
    },
    {
      label: 'Capital Gains Tax (Rent Scenario)',
      description: 'Tax on appreciation since inheritance',
      unit: 'currency',
      formula: 'max(0, {propertyValue.year10} - {stepUpBasis}) × {capitalGainsRate}/100',
    }
  ),

  // Selling costs at exit
  sellingCostsAtExit: computed(
    'sellingCostsAtExit',
    [temporalId('propertyValue', 10), 'sellingCosts'],
    (pv, rate) => pv * rate / 100,
    {
      label: 'Selling Costs at Exit',
      unit: 'currency',
      formula: '{propertyValue.year10} × {sellingCosts}/100',
    }
  ),

  // Total equity in rent scenario (property + portfolio)
  rentTotalEquity: computed(
    'rentTotalEquity',
    [temporalId('propertyValue', 10), temporalId('investedRentalPortfolio', 10), 'rentalSetupCosts'],
    (pv, portfolio, setup) => pv + portfolio - setup,
    {
      label: 'Total Equity (Rent Scenario)',
      description: 'Property value plus invested rental income minus setup costs',
      unit: 'currency',
      formula: '{propertyValue.year10} + {investedRentalPortfolio.year10} - {rentalSetupCosts}',
    }
  ),

  // Net liquidation value - rent scenario
  rentNetLiquidation: computed(
    'rentNetLiquidation',
    ['rentTotalEquity', 'depreciationRecaptureTax', 'rentCapitalGainsTax', 'sellingCostsAtExit'],
    (equity, depRecap, capGains, selling) => equity - depRecap - capGains - selling,
    {
      label: 'Net Liquidation (Rent Scenario)',
      description: 'What you walk away with after all taxes and costs',
      unit: 'currency',
      formula: '{rentTotalEquity} - {depreciationRecaptureTax} - {rentCapitalGainsTax} - {sellingCostsAtExit}',
    }
  ),

  // Capital gains on portfolio - sell scenario (80/20)
  sellCapitalGainsTax: computed(
    'sellCapitalGainsTax',
    [temporalId('portfolio80_20', 10), 'initialSaleProceeds', 'capitalGainsRate'],
    (portfolio, basis, rate) => {
      const gain = portfolio - basis;
      return gain > 0 ? gain * rate / 100 : 0;
    },
    {
      label: 'Capital Gains Tax (Sell Scenario)',
      description: 'Tax on portfolio gains at exit',
      unit: 'currency',
      formula: 'max(0, {portfolio80_20.year10} - {initialSaleProceeds}) × {capitalGainsRate}/100',
    }
  ),

  // Net liquidation value - sell scenario (80/20)
  sellNetLiquidation: computed(
    'sellNetLiquidation',
    [temporalId('portfolio80_20', 10), 'sellCapitalGainsTax'],
    (portfolio, capGains) => portfolio - capGains,
    {
      label: 'Net Liquidation (Sell Scenario)',
      description: 'Portfolio value minus capital gains tax',
      unit: 'currency',
      formula: '{portfolio80_20.year10} - {sellCapitalGainsTax}',
    }
  ),

  // Rent advantage (positive = rent is better)
  rentAdvantage: computed(
    'rentAdvantage',
    ['rentNetLiquidation', 'sellNetLiquidation'],
    (rent, sell) => rent - sell,
    {
      label: 'Rent Advantage',
      description: 'How much better rent scenario is vs sell (can be negative)',
      unit: 'currency',
      formula: '{rentNetLiquidation} - {sellNetLiquidation}',
    }
  ),
};

// ============================================================================
// COMBINE ALL COMPUTED NODES
// ============================================================================

export const computedNodes: Record<string, ComputedNode> = {
  ...buildingNodes,
  ...taxDragNodes,
  ...propertyValueNodes,
  ...assessedValueNodes,
  ...rentalNodes,
  ...cumulativeNodes,
  ...sellScenarioNodes,
  ...exitNodes,
};

export default computedNodes;
